import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Toast } from "primereact/toast";
import {
  FaUserShield,
  FaPlus,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaEdit,
  FaTrash,
  FaKey,
  FaCopy,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaExclamationTriangle,
  FaRupeeSign,
  FaBullhorn,
  FaUserLock,
  FaWhatsapp,
  FaMagic,
  FaPowerOff,
  FaInbox,
  FaLock,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";
import { PERMISSION_GROUPS, ALL_PERMISSION_KEYS, PERMISSION_BY_KEY, expandAccess } from "../Data/menu";

/* ================= styles ================= */

const CSS = `
@keyframes saFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes saFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes saSlide { from { transform: translateX(100%); } to { transform: none; } }
@keyframes saZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes saBar { from { width: 0; } }
.sa-in { animation: saFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.sa-fade { animation: saFade .2s ease both; }
.sa-slide { animation: saSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.sa-zoom { animation: saZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.sa-bar { animation: saBar .9s cubic-bezier(.2,.7,.2,1) both; }
.sa-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.sa-press:hover { transform: translateY(-1px); }
.sa-press:active { transform: scale(.96); }
.sa-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.sa-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px -20px rgba(15,23,42,.45); }
.sa-knob { transition: transform .25s cubic-bezier(.2,.8,.2,1); }
@media (prefers-reduced-motion: reduce) {
  .sa-in, .sa-fade, .sa-slide, .sa-zoom, .sa-bar { animation: none; }
  .sa-press:hover, .sa-press:active, .sa-card:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500";

const PANEL_URL = "https://admin.ycnp.in";

const PRESETS = [
  { id: "support", label: "Support desk", desc: "Reports and user issues", keys: ["report_request", "user_message_report", "group_message_report", "all_users", "admin_request"] },
  { id: "content", label: "Content manager", desc: "Ads, posts, links and videos", keys: ["app_ads", "owner_post", "owner_links", "youtube", "youtube_videos", "youtube_shorts", "instagram_reels", "facebook_videos", "facebook_reels"] },
  { id: "groups", label: "Group manager", desc: "Groups, joins and members", keys: ["group_list", "group_joining_req", "all_group_members_list", "group_message_report", "group_package_config"] },
  { id: "finance", label: "Finance manager", desc: "Wallets, payouts and paid joins", keys: ["wallet_list", "withdrawal_request", "ads_reward_wallet", "group_joining_req", "customer_request"] },
  { id: "customers", label: "Customer manager", desc: "Customers and their links", keys: ["customer_list", "customer_request", "customer_packages", "active_youtube_links", "active_facebook_links"] },
  { id: "all", label: "Everything", desc: "All pages except System Users", keys: ALL_PERMISSION_KEYS },
];

const RISK = {
  money: { label: "Money", icon: FaRupeeSign, cls: "bg-amber-100 text-amber-800" },
  sensitive: { label: "Sensitive", icon: FaUserLock, cls: "bg-rose-100 text-rose-700" },
  broadcast: { label: "All users", icon: FaBullhorn, cls: "bg-violet-100 text-violet-700" },
};

const EMPTY = { owner_id: "", first_name: "", last_name: "", email_id: "", password: "", status: 1, access: [] };

/* ================= helpers ================= */

const parseAccess = (a) => {
  if (Array.isArray(a)) return a;
  try {
    const v = JSON.parse(a || "[]");
    return Array.isArray(v) ? v : [];
  } catch (e) {
    return [];
  }
};
const fullName = (s) => `${s.first_name || ""} ${s.last_name || ""}`.trim();
const isActive = (s) => String(s.status) === "1";
const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const sym = "@#$%&*";
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  let p = Array.from(arr.slice(0, 10), (n) => chars[n % chars.length]).join("");
  p += sym[arr[10] % sym.length] + String(arr[11] % 10);
  return p;
};
const strength = (p) => {
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return s;
};

const Avatar = ({ name, size = 48, off }) => {
  const t =
    String(name || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <span className={`rounded-2xl shrink-0 flex items-center justify-center font-bold text-white ${off ? "bg-slate-400" : "bg-gradient-to-br from-teal-500 to-cyan-700"}`} style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {t}
    </span>
  );
};

/* ================= page ================= */

export default function SubAdminManager() {
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();
  const API_URL = `${baseUrl}/subadmin_api.php`;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");

  const [editor, setEditor] = useState(null); // 'add' | 'edit'
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [showPass, setShowPass] = useState(false);
  const [permSearch, setPermSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);
  const [credentials, setCredentials] = useState(null); // {name, email, password}

  const toast = useRef(null);

  /* ----- API (same actions) ----- */

  const call = useCallback(
    async (action, data) => {
      const opts = { method: data ? "POST" : "GET", credentials: "include" };
      if (data) {
        const fd = new FormData();
        Object.entries(data).forEach(([k, v]) => fd.append(k, v));
        opts.body = fd;
      }
      const res = await fetch(`${API_URL}?action=${action}`, opts);
      const text = await res.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error("Server sent a wrong reply");
      }
    },
    [API_URL]
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await call("list");
      if (Array.isArray(res)) setList(res);
      else throw new Error(res?.message || "Could not load managers");
    } catch (e) {
      setLoadError(e.message);
    }
    setLoading(false);
  }, [call]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /* ----- derived ----- */

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => list.filter((s) => !q || [fullName(s), s.email_id, s.owner_id].map((v) => String(v || "").toLowerCase()).some((v) => v.includes(q))),
    [list, q]
  );
  const stats = useMemo(
    () => ({
      total: list.length,
      active: list.filter(isActive).length,
      money: list.filter((s) => expandAccess(parseAccess(s.access)).some((k) => PERMISSION_BY_KEY[k]?.risk === "money")).length,
    }),
    [list]
  );

  const errors = useMemo(() => {
    const e = {};
    if (!String(form.first_name).trim()) e.first_name = "First name is required";
    if (!/^\S+@\S+\.\S+$/.test(String(form.email_id).trim())) e.email_id = "Enter a valid email";
    if (editor === "add" && String(form.password).length < 8) e.password = "At least 8 characters";
    if (editor === "edit" && form.password && String(form.password).length < 8) e.password = "At least 8 characters (or leave empty)";
    if (!form.access.length) e.access = "Give at least one page";
    const dup = list.some((s) => s.owner_id !== form.owner_id && String(s.email_id || "").toLowerCase() === String(form.email_id).trim().toLowerCase());
    if (dup) e.email_id = "Another manager already uses this email";
    return e;
  }, [form, editor, list]);

  /* ----- editor ----- */

  const openAdd = () => {
    setForm({ ...EMPTY, password: genPassword() });
    setTouched({});
    setShowPass(true);
    setPermSearch("");
    setEditor("add");
  };
  const openEdit = (s, focusPassword) => {
    setForm({ owner_id: s.owner_id, first_name: s.first_name || "", last_name: s.last_name || "", email_id: s.email_id || "", password: focusPassword ? genPassword() : "", status: isActive(s) ? 1 : 0, access: expandAccess(parseAccess(s.access)) });
    setTouched({});
    setShowPass(!!focusPassword);
    setPermSearch("");
    setEditor("edit");
  };

  const toggleKey = (k) => setForm((f) => ({ ...f, access: f.access.includes(k) ? f.access.filter((x) => x !== k) : [...f.access, k] }));
  const toggleGroup = (g) => {
    const keys = g.items.map((i) => i.key);
    const all = keys.every((k) => form.access.includes(k));
    setForm((f) => ({ ...f, access: all ? f.access.filter((k) => !keys.includes(k)) : Array.from(new Set([...f.access, ...keys])) }));
  };
  const applyPreset = (p) => setForm((f) => ({ ...f, access: Array.from(new Set([...f.access, ...p.keys])) }));

  const save = async () => {
    setTouched({ first_name: 1, email_id: 1, password: 1, access: 1 });
    if (Object.keys(errors).length) {
      toast.current?.show({ severity: "error", summary: "Check the form", detail: Object.values(errors)[0] });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        owner_id: form.owner_id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email_id: form.email_id.trim(),
        password: form.password,
        role: "subadmin",
        status: form.status,
        access: JSON.stringify(form.access.filter((k) => ALL_PERMISSION_KEYS.includes(k))),
      };
      const res = await call(editor === "edit" ? "update" : "create", payload);
      if (!res?.success) throw new Error(res?.message || "Could not save");
      toast.current?.show({ severity: "success", summary: "Saved", detail: res.message });
      if (editor === "add" || form.password) setCredentials({ name: `${payload.first_name} ${payload.last_name}`.trim(), email: payload.email_id, password: form.password });
      setEditor(null);
      fetchList();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setSaving(false);
  };

  const quickStatus = async (s) => {
    try {
      const res = await call("update", {
        owner_id: s.owner_id,
        first_name: s.first_name || "",
        last_name: s.last_name || "",
        email_id: s.email_id || "",
        password: "",
        status: isActive(s) ? 0 : 1,
        access: JSON.stringify(parseAccess(s.access)),
      });
      if (!res?.success) throw new Error(res?.message || "Failed");
      toast.current?.show({ severity: "success", summary: isActive(s) ? "Deactivated" : "Activated", detail: isActive(s) ? "They can no longer log in" : "They can log in again" });
      fetchList();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
  };

  const runDelete = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      const res = await call("delete", { owner_id: confirm.owner_id });
      if (!res?.success) throw new Error(res?.message || "Failed");
      toast.current?.show({ severity: "success", summary: "Removed", detail: "Manager removed" });
      setConfirm(null);
      fetchList();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message });
    }
    setWorking(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (credentials) setCredentials(null);
      else if (confirm && !working) setConfirm(null);
      else if (editor && !saving) setEditor(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [credentials, confirm, working, editor, saving]);

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, life: 1500 });
  };

  const loginMessage = (c) => `YCNP Admin Panel login\n\nLink: ${PANEL_URL}\nEmail: ${c.email}\nPassword: ${c.password}\n\nPlease keep this private.`;

  const pStrength = strength(String(form.password || ""));
  const ps = permSearch.trim().toLowerCase();

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="sa-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">System Users</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Managers & Permissions</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Create a login for a team member and choose exactly which pages they can open.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchList} disabled={loading} className="sa-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={openAdd} className="sa-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold shadow-sm">
            <FaPlus size={11} /> Add Manager
          </button>
        </div>
      </div>

      {loadError && (
        <div className="sa-in mb-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 dark:bg-rose-900/20 p-4 text-sm text-rose-900 dark:text-rose-200">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Could not load managers</p>
            <p className="text-xs">{loadError}</p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {[
          { label: "Managers", value: stats.total, icon: FaUserShield, tint: "bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" },
          { label: "Can log in now", value: stats.active, icon: FaPowerOff, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
          { label: "Have money permissions", value: stats.money, icon: FaRupeeSign, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} sa-in p-4 flex items-center gap-3`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">{loading && !list.length ? "–" : c.value}</p>
                <p className="text-xs text-slate-500">{c.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className={`${CARD} sa-in p-3 mb-4`} style={{ animationDelay: "180ms" }}>
        <div className="relative">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={13} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email or ID" className={`${INPUT} h-10 pl-10 pr-9`} />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400" aria-label="Clear search">
              <FaTimes size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Cards */}
      {loading && !list.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${CARD} h-64 animate-pulse`} />
          ))}
        </div>
      ) : !shown.length ? (
        <div className={`${CARD} px-5 py-16 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{list.length ? "No manager matches" : "No managers yet"}</p>
          {!list.length && (
            <button type="button" onClick={openAdd} className="sa-press mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-teal-600 text-white text-sm font-semibold">
              <FaPlus size={11} /> Add the first manager
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
          {shown.map((s, i) => {
            const acc = expandAccess(parseAccess(s.access)).filter((k) => ALL_PERMISSION_KEYS.includes(k));
            const risky = acc.filter((k) => PERMISSION_BY_KEY[k]?.risk);
            const name = fullName(s);
            return (
              <article key={s.owner_id} className={`sa-card sa-in rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden flex flex-col ${isActive(s) ? "border-slate-200 dark:border-slate-700" : "border-slate-200 dark:border-slate-700 opacity-80"}`} style={{ animationDelay: `${Math.min(i, 10) * 50}ms` }}>
                <div className="p-4 flex items-start gap-3">
                  <Avatar name={name || s.email_id} off={!isActive(s)} />
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${name ? "text-slate-900 dark:text-white" : "text-rose-600 italic"}`}>{name || "No name set"}</p>
                    <button type="button" onClick={() => s.email_id && copyText(s.email_id, "Email copied")} className={`text-xs truncate max-w-full inline-flex items-center gap-1 ${s.email_id ? "text-slate-500 hover:text-slate-800" : "text-rose-600 italic"}`}>
                      {s.email_id || "No email: cannot log in"} {s.email_id && <FaCopy size={8} />}
                    </button>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{s.owner_id}</p>
                  </div>
                  <button type="button" onClick={() => quickStatus(s)} role="switch" aria-checked={isActive(s)} title={isActive(s) ? "Active: click to deactivate" : "Inactive: click to activate"} className={`relative w-12 h-7 rounded-full shrink-0 transition-colors ${isActive(s) ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}>
                    <span className={`sa-knob absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow ${isActive(s) ? "translate-x-5" : ""}`} />
                  </button>
                </div>

                {(!name || !s.email_id) && (
                  <p className="mx-4 mb-2 flex items-center gap-1.5 rounded-lg bg-rose-50 dark:bg-rose-900/20 px-2.5 py-1.5 text-[11px] text-rose-700">
                    <FaExclamationTriangle size={10} /> Details are missing. Click Edit to complete them.
                  </p>
                )}

                <div className="px-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-500">Pages allowed</span>
                    <b className="tabular-nums text-slate-900 dark:text-white">
                      {acc.length} / {ALL_PERMISSION_KEYS.length}
                    </b>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    <div className="sa-bar h-full rounded-full bg-teal-500" style={{ width: `${(acc.length / ALL_PERMISSION_KEYS.length) * 100}%` }} />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {acc.slice(0, 8).map((k) => {
                      const p = PERMISSION_BY_KEY[k];
                      const R = p?.risk ? RISK[p.risk] : null;
                      return (
                        <span key={k} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${R ? R.cls : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"}`}>
                          {R && <R.icon size={8} />} {p?.title || k}
                        </span>
                      );
                    })}
                    {acc.length > 8 && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">+{acc.length - 8} more</span>}
                    {!acc.length && <span className="text-xs text-slate-400">No pages yet (only Dashboard)</span>}
                  </div>
                  {risky.length > 0 && (
                    <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
                      <FaExclamationTriangle size={9} /> {risky.length} sensitive or money page{risky.length > 1 ? "s" : ""}
                    </p>
                  )}
                </div>

                <div className="mt-auto pt-4 grid grid-cols-3 border-t border-slate-100 dark:border-slate-700">
                  <button type="button" onClick={() => openEdit(s)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                    <FaEdit size={11} /> Edit
                  </button>
                  <button type="button" onClick={() => openEdit(s, true)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors border-x border-slate-100 dark:border-slate-700">
                    <FaKey size={11} /> New password
                  </button>
                  <button type="button" onClick={() => setConfirm(s)} className="h-11 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors">
                    <FaTrash size={11} /> Remove
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ================= Editor ================= */}
      {editor && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="sa-fade absolute inset-0 bg-slate-900/40" onClick={() => !saving && setEditor(null)} />
          <aside className="sa-slide relative w-full max-w-4xl h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500">{editor === "edit" ? form.owner_id : "New manager"}</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{editor === "edit" ? "Edit manager" : "Add a manager"}</h2>
              </div>
              <button type="button" onClick={() => !saving && setEditor(null)} className="sa-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-5">
                {/* Left: details */}
                <div className="lg:col-span-2 space-y-4">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">1. Who is it?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="sa-fn" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        First name *
                      </label>
                      <input id="sa-fn" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} onBlur={() => setTouched((t) => ({ ...t, first_name: 1 }))} className={`${INPUT} h-10 px-3`} />
                      {touched.first_name && errors.first_name && <p className="text-[11px] text-rose-600 mt-1">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="sa-ln" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                        Last name
                      </label>
                      <input id="sa-ln" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className={`${INPUT} h-10 px-3`} />
                    </div>
                  </div>

                  <p className="text-sm font-semibold text-slate-900 dark:text-white pt-2">2. Login details</p>
                  <div>
                    <label htmlFor="sa-em" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                      Email (used as user ID) *
                    </label>
                    <input id="sa-em" type="email" value={form.email_id} onChange={(e) => setForm((f) => ({ ...f, email_id: e.target.value }))} onBlur={() => setTouched((t) => ({ ...t, email_id: 1 }))} placeholder="name@example.com" className={`${INPUT} h-10 px-3`} />
                    {touched.email_id && errors.email_id && <p className="text-[11px] text-rose-600 mt-1">{errors.email_id}</p>}
                  </div>
                  <div>
                    <label htmlFor="sa-pw" className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                      Password {editor === "add" ? "*" : "(leave empty to keep the old one)"}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input id="sa-pw" type={showPass ? "text" : "password"} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} onBlur={() => setTouched((t) => ({ ...t, password: 1 }))} className={`${INPUT} h-10 pl-3 pr-9 font-mono`} autoComplete="new-password" />
                        <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-label={showPass ? "Hide password" : "Show password"}>
                          {showPass ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                        </button>
                      </div>
                      <button type="button" onClick={() => { setForm((f) => ({ ...f, password: genPassword() })); setShowPass(true); }} title="Make a strong password" className="sa-press h-10 px-3 rounded-xl border border-slate-200 dark:border-slate-600 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200">
                        <FaMagic size={10} /> New
                      </button>
                      {form.password && (
                        <button type="button" onClick={() => copyText(form.password, "Password copied")} className="sa-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center text-slate-600" aria-label="Copy password">
                          <FaCopy size={11} />
                        </button>
                      )}
                    </div>
                    {form.password && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 flex gap-1">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <span key={i} className={`h-1.5 flex-1 rounded-full ${i < pStrength ? (pStrength >= 4 ? "bg-emerald-500" : pStrength >= 3 ? "bg-amber-500" : "bg-rose-500") : "bg-slate-200 dark:bg-slate-700"}`} />
                          ))}
                        </div>
                        <span className="text-[11px] text-slate-500">{pStrength >= 4 ? "Strong" : pStrength >= 3 ? "Okay" : "Weak"}</span>
                      </div>
                    )}
                    {touched.password && errors.password && <p className="text-[11px] text-rose-600 mt-1">{errors.password}</p>}
                  </div>

                  <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Can log in</p>
                      <p className="text-xs text-slate-500">Turn off to pause this account without deleting it</p>
                    </div>
                    <button type="button" role="switch" aria-checked={form.status === 1} onClick={() => setForm((f) => ({ ...f, status: f.status === 1 ? 0 : 1 }))} className={`relative w-12 h-7 rounded-full transition-colors ${form.status === 1 ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <span className={`sa-knob absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow ${form.status === 1 ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  <p className="flex items-start gap-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-3 text-xs text-slate-600 dark:text-slate-300">
                    <FaLock className="mt-0.5 shrink-0 text-teal-600" /> Managers never see the System Users page, so they cannot create logins or change their own permissions.
                  </p>
                </div>

                {/* Right: permissions */}
                <div className="lg:col-span-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">3. What can they open?</p>
                    <span className="text-xs font-bold tabular-nums text-teal-700 dark:text-teal-300">
                      {form.access.length} / {ALL_PERMISSION_KEYS.length} pages
                    </span>
                  </div>
                  {touched.access && errors.access && <p className="text-[11px] text-rose-600 mt-1">{errors.access}</p>}

                  <p className="text-xs text-slate-500 mt-2 mb-1.5">Quick start (adds these pages):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {PRESETS.map((p) => (
                      <button key={p.id} type="button" onClick={() => applyPreset(p)} title={p.desc} className="sa-press h-8 px-3 rounded-full border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:border-teal-400 hover:text-teal-700">
                        + {p.label}
                      </button>
                    ))}
                    {form.access.length > 0 && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, access: [] }))} className="h-8 px-3 rounded-full text-xs font-semibold text-rose-600 hover:bg-rose-50">
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="relative mt-3">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={11} />
                    <input value={permSearch} onChange={(e) => setPermSearch(e.target.value)} placeholder="Find a page" className={`${INPUT} h-9 pl-8 pr-3`} />
                  </div>

                  <div className="mt-3 space-y-3">
                    {PERMISSION_GROUPS.map((g) => {
                      const items = g.items.filter((it) => !ps || it.title.toLowerCase().includes(ps) || String(it.desc).toLowerCase().includes(ps));
                      if (!items.length) return null;
                      const on = g.items.filter((it) => form.access.includes(it.key)).length;
                      const all = on === g.items.length;
                      return (
                        <section key={g.title} className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                          <button type="button" onClick={() => toggleGroup(g)} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 text-left">
                            <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
                              <span className={`w-4 h-4 rounded border-2 flex items-center justify-center ${all ? "bg-teal-600 border-teal-600" : on ? "border-teal-600" : "border-slate-300"}`}>
                                {all ? <FaCheck size={8} className="text-white" /> : on ? <span className="w-2 h-0.5 bg-teal-600" /> : null}
                              </span>
                              {g.title}
                            </span>
                            <span className="text-[11px] text-slate-500 tabular-nums">
                              {on}/{g.items.length}
                            </span>
                          </button>
                          <div className="divide-y divide-slate-100 dark:divide-slate-700">
                            {items.map((it) => {
                              const checked = form.access.includes(it.key);
                              const R = it.risk ? RISK[it.risk] : null;
                              return (
                                <label key={it.key} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? "bg-teal-50/60 dark:bg-teal-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
                                  <input type="checkbox" checked={checked} onChange={() => toggleKey(it.key)} className="w-4 h-4 accent-teal-600" />
                                  <span className="w-5 text-center text-slate-500">{it.icon}</span>
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-medium text-slate-900 dark:text-white">{it.title}</span>
                                    <span className="block text-[11px] text-slate-500 truncate">{it.desc}</span>
                                  </span>
                                  {R && (
                                    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${R.cls}`}>
                                      <R.icon size={8} /> {R.label}
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <span className="text-xs text-slate-500 hidden sm:inline">{form.access.some((k) => PERMISSION_BY_KEY[k]?.risk === "money") ? "⚠ This manager can handle money" : ""}</span>
              <div className="flex gap-2 ml-auto">
                <button type="button" onClick={() => setEditor(null)} disabled={saving} className="sa-press h-10 px-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button type="button" onClick={save} disabled={saving} className="sa-press inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold disabled:opacity-60">
                  {saving ? <FaSyncAlt className="animate-spin" size={12} /> : <FaCheck size={11} />}
                  {saving ? "Saving..." : editor === "edit" ? "Save changes" : "Create login"}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ================= Login details ================= */}
      {credentials && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="sa-fade absolute inset-0 bg-slate-900/60" onClick={() => setCredentials(null)} />
          <div className="sa-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <FaCheck size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">Login ready for {credentials.name || credentials.email}</h3>
            <p className="text-sm text-slate-500 mt-1">Send these details privately. The password will not be shown again.</p>
            <div className="mt-4 rounded-xl bg-slate-900 text-white p-4 font-mono text-sm space-y-1.5">
              <p>
                <span className="text-slate-400">Link: </span>
                {PANEL_URL}
              </p>
              <p>
                <span className="text-slate-400">Email: </span>
                {credentials.email}
              </p>
              <p>
                <span className="text-slate-400">Password: </span>
                {credentials.password}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button type="button" onClick={() => copyText(loginMessage(credentials), "Login details copied")} className="sa-press h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 inline-flex items-center justify-center gap-2">
                <FaCopy size={12} /> Copy all
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent(loginMessage(credentials))}`} target="_blank" rel="noreferrer" className="sa-press h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                <FaWhatsapp /> Send on WhatsApp
              </a>
            </div>
            <button type="button" onClick={() => setCredentials(null)} className="w-full mt-2 h-10 text-sm text-slate-500 hover:underline">
              Done
            </button>
          </div>
        </div>
      )}

      {/* ================= Delete ================= */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="sa-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="sa-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center">
              <FaTrash size={16} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">Remove {fullName(confirm) || confirm.email_id || "this manager"}?</h3>
            <p className="text-sm text-slate-500 mt-1">Their login stops working straight away. To pause them for a while, turn off "Can log in" instead.</p>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="sa-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Keep
              </button>
              <button type="button" onClick={runDelete} disabled={working} className="sa-press flex-1 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Removing..." : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}